import React from 'react'
import { getTranslation } from '../translations'

// Color mapping: low=red, medium=green, high=yellow (matching reference)
const COLORS = {
  low: 'bg-red-500',
  medium: 'bg-green-500',
  high: 'bg-yellow-500',
}

function SectionHeader({ title, subtitle }) {
  return (
    <div className="mb-2">
      <div className="bg-[#134225] text-white font-extrabold px-3 py-2 uppercase tracking-wide text-sm">{title}</div>
      {subtitle ? (
        <div className="text-[12px] italic text-[#334c3e] px-2 py-1">{subtitle}</div>
      ) : null}
    </div>
  )
}

// Continuous bar with active portion highlighted
function RatingScale({ level = 'medium', t }) {
  const barWidth = level === 'low' ? '33%' : level === 'medium' ? '66%' : '100%'
  const color = COLORS[level] || 'bg-gray-300'
  
  return (
    <div className="w-full max-w-[280px]">
      <div className="relative h-3 w-full rounded-full bg-gray-200 overflow-hidden">
        <div className={`absolute left-0 top-0 bottom-0 rounded-full ${color}`} style={{ width: barWidth }} />
      </div>
      {/* labels */}
      <div className="mt-2 flex justify-between text-xs text-gray-600">
        <span>{t.low}</span>
        <span>{t.medium}</span>
        <span>{t.high}</span>
      </div>
    </div>
  )
}

function Row({ item, t }) {
  return (
    <tr className="odd:bg-white even:bg-gray-50 text-sm align-top">
      <td className="px-3 py-3 border border-[#e4ebe7] font-semibold">{item.name}</td>
      <td className="px-3 py-3 border border-[#e4ebe7]"><RatingScale level={item.level} t={t} /></td>
      <td className="px-3 py-3 border border-[#e4ebe7]">{item.meaning}</td>
      <td className="px-3 py-3 border border-[#e4ebe7]">{item.function}</td>
    </tr>
  )
}

function Legend({ t }) {
  return (
    <div className="flex items-center justify-center gap-6 text-sm text-gray-700 mt-4">
      <div className="flex items-center gap-2"><span className="h-3 w-3 rounded-full bg-red-500 inline-block" /> {t.low}</div>
      <div className="flex items-center gap-2"><span className="h-3 w-3 rounded-full bg-green-500 inline-block" /> {t.medium}</div>
      <div className="flex items-center gap-2"><span className="h-3 w-3 rounded-full bg-yellow-500 inline-block" /> {t.high}</div>
    </div>
  )
}

export default function DetailedInsights({
  titleA,
  subtitleA,
  items = [],
  titleB,
  subtitleB,
  derivedItems = [],
  note,
  language = 'english',
}) {
  const t = getTranslation(language)
  const sectionTitleA = titleA || t.section2Title
  const sectionSubtitleA = subtitleA || t.section2Subtitle
  const sectionTitleB = titleB || t.derivedParametersSubtitle
  const noteText = note || t.phosphorusNote
  
  return (
    <div className="mt-6">
      {/* Table A */}
      <SectionHeader title={sectionTitleA} subtitle={sectionSubtitleA} />
      <div className="border border-[#cbd9d2] rounded-md overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-[#134225] text-white text-sm">
              <th className="px-3 py-2 border border-[#d9e4dd] text-left">{t.parameter}</th>
              <th className="px-3 py-2 border border-[#d9e4dd] text-left">{t.fertilityRating}</th>
              <th className="px-3 py-2 border border-[#d9e4dd] text-left">{t.whatItMeans}</th>
              <th className="px-3 py-2 border border-[#d9e4dd] text-left">{t.functionInCropGrowth}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <Row key={it.name} item={it} t={t} />
            ))}
          </tbody>
        </table>
      </div>

      {/* Table B */}
      {derivedItems.length > 0 && (
        <div className="mt-6">
          <SectionHeader title={sectionTitleB} subtitle={t.derivedParametersSubtitle} />
          <div className="border border-[#cbd9d2] rounded-md overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-[#134225] text-white text-sm">
                  <th className="px-3 py-2 border border-[#d9e4dd] text-left">{t.parameter}</th>
                  <th className="px-3 py-2 border border-[#d9e4dd] text-left">{t.fertilityRating}</th>
                  <th className="px-3 py-2 border border-[#d9e4dd] text-left">{t.whatItMeans}</th>
                  <th className="px-3 py-2 border border-[#d9e4dd] text-left">{t.functionInCropGrowth}</th>
                </tr>
              </thead>
              <tbody>
                {derivedItems.map((it) => (
                  <Row key={`d-${it.name}`} item={it} t={t} />
                ))}
              </tbody>
            </table>
          </div>
          <Legend t={t} />
          {noteText && <p className="text-sm text-gray-700 mt-4">{noteText}</p>}
        </div>
      )}
    </div>
  )}
